import { useApiClient } from "@/infrastructure/rest";
import { StationDataDto } from "@/shared/types";
import { usePrefetchQuery, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Station, StationNameFormat } from "./domain";

const STATIONS_QUERY_KEY = "stations";

export function useGetStations<T = Station[]>(options?: Omit<UseQueryOptions<Station[], Error, T>, "queryFn" | "queryKey">) {
    const apiClient = useApiClient();

    return useQuery({
        ...getStationQuery(apiClient),
        ...options,
    });
}

export function usePrefetchStations() {
    const apiClient = useApiClient();
    usePrefetchQuery(getStationQuery(apiClient));
}

function getStationQuery(apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [STATIONS_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<StationDataDto>(`/infra/stations`);
            const stations: Station[] = data ? Object.values(data.stationByCode).map((stationDto) => Station.fromDto(stationDto)) : [];
            return stations.sort(Station.sort);
        },
        staleTime: Infinity,
    };
}

/**
 * Returns a memoized lookup function to get station display names.
 * Uses Map-based lookup.
 *
 * @param format - Display format:
 *   - "withoutStationCode": Just the station name (e.g., "Amsterdam Centraal")
 *   - "stationCodeSuffixed": Name with code suffix (e.g., "Amsterdam Centraal (Asd)")
 *   - "stationCodePrefixed": Code with name suffix (e.g., "Asd (Amsterdam Centraal)")
 *
 * @returns A function that takes a station code and returns the formatted display name.
 *          Returns the code itself as fallback if station is not found.
 */
export function useGetStationDisplayName(format: StationNameFormat = "withoutStationCode"): (code: string) => string {
    const { data: stations } = useGetStations();

    const lookup = useMemo(() => {
        if (!stations) return new Map<string, string>();
        return new Map(stations.map((s) => [s.code.toLowerCase(), Station.getDisplayName(s, format)]));
    }, [stations, format]);

    return useCallback((code: string) => lookup.get(code.toLowerCase()) ?? code, [lookup]);
}

type UseSortByStationDisplayNameOptions<K extends string> = {
    stationCodeKey?: K;
    format?: StationNameFormat;
};

/**
 * Returns a memoized, display-name-sorted copy of the provided items.
 *
 * Uses station display names (resolved from station codes) as the sort key.
 *
 * @typeParam T - Item shape that contains a station code property.
 * @param items - The items to sort.
 * @param options - Optional configuration for the station code key and display-name format.
 * @returns A new array sorted by station display name.
 */
export function useSortByStationDisplayName<T extends Record<K, string>, K extends string = "stationCode">(
    items: T[],
    { format, stationCodeKey }: UseSortByStationDisplayNameOptions<K> = {}
): T[] {
    const getStationDisplayName = useGetStationDisplayName(format);

    return useMemo(() => {
        return items.toSorted(Station.createSortByDisplayName(getStationDisplayName, stationCodeKey));
    }, [items, getStationDisplayName, stationCodeKey]);
}
